import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, ArrowLeft, Save, Trash2, Copy, Star, ChevronDown, ChevronUp,
  GripVertical, ToggleLeft, ToggleRight, FileText, X, Eye,
} from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import '../../pages/Pages.css';

// --- Interfaces ---

interface TemplateField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'select' | 'checkbox' | 'date' | 'signature';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  prefilled_from?: string;
}

interface TemplateSection {
  id: string;
  title: string;
  enabled: boolean;
  required?: boolean;
  order: number;
  text: string;
  fields: TemplateField[];
}

interface ContractTemplate {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_system: boolean;
  sections: TemplateSection[];
  created_at: string;
  updated_at: string;
}

// --- Field type labels ---

const fieldTypeLabels: Record<string, string> = {
  text: 'Textfeld',
  textarea: 'Mehrzeiliges Textfeld',
  number: 'Zahlenfeld',
  select: 'Dropdown',
  checkbox: 'Ja/Nein',
  date: 'Datum',
  signature: 'Unterschrift',
};

// --- Component ---

export default function VertragsvorlagenEditor() {
  const { showToast } = useToast();
  // List view state
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Editor view state
  const [editingTemplate, setEditingTemplate] = useState<ContractTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [sections, setSections] = useState<TemplateSection[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Preview state
  const [previewTemplate, setPreviewTemplate] = useState<ContractTemplate | null>(null);

  // Drag & drop state
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // --- Load templates ---

  const loadTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/contract-templates');
      const loaded = (res.data?.items || res.data || []).map((t: any) => ({
        ...t,
        sections: typeof t.sections === 'string' ? JSON.parse(t.sections) : (t.sections || []),
      }));
      setTemplates(loaded);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // --- List view actions ---

  const handleDuplicate = async (id: string) => {
    try {
      await api.post(`/contract-templates/${id}/duplicate`);
      await loadTemplates();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Duplizieren', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    try {
      await api.delete(`/contract-templates/${id}`);
      await loadTemplates();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Löschen', 'error');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await api.post(`/contract-templates/${id}/set-default`);
      showToast('Als Standard-Vorlage gesetzt', 'success');
      await loadTemplates();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Setzen als Standard', 'error');
    }
  };

  const handlePreview = async (id: string) => {
    try {
      const res = await api.get(`/contract-templates/${id}`);
      const t = res.data;
      const parsedSections: TemplateSection[] = typeof t.sections === 'string'
        ? JSON.parse(t.sections)
        : (t.sections || []);
      setPreviewTemplate({ ...t, sections: parsedSections });
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Laden der Vorlage', 'error');
    }
  };

  const handleEdit = async (id: string) => {
    try {
      const res = await api.get(`/contract-templates/${id}`);
      const t = res.data;
      const parsedSections: TemplateSection[] = typeof t.sections === 'string'
        ? JSON.parse(t.sections)
        : (t.sections || []);
      setEditingTemplate({ ...t, sections: parsedSections });
      setTemplateName(t.name || '');
      setTemplateDescription(t.description || '');
      setIsDefault(t.is_default || false);
      setSections(parsedSections);
      setExpandedSections({});
      setSaveMessage('');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Laden der Vorlage', 'error');
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setIsDefault(false);
    setSections([]);
    setExpandedSections({});
    setSaveMessage('');
  };

  const handleBackToList = () => {
    setEditingTemplate(null);
    setIsCreating(false);
    setSaveMessage('');
    loadTemplates();
  };

  // --- Editor: Save ---

  const handleSave = async () => {
    if (!templateName.trim()) {
      showToast('Bitte einen Namen für die Vorlage eingeben.', 'info');
      return;
    }
    setSaving(true);
    setSaveMessage('');
    try {
      const orderedSections = sections.map((s, i) => ({ ...s, order: i + 1 }));
      const payload = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        sections: JSON.stringify(orderedSections),
      };

      if (isCreating) {
        const res = await api.post('/contract-templates', payload);
        if (isDefault) {
          await api.post(`/contract-templates/${res.data.id}/set-default`);
        }
        setEditingTemplate({ ...res.data, sections: orderedSections });
        setIsCreating(false);
      } else if (editingTemplate) {
        await api.put(`/contract-templates/${editingTemplate.id}`, payload);
        if (isDefault && !editingTemplate.is_default) {
          await api.post(`/contract-templates/${editingTemplate.id}/set-default`);
        }
        setEditingTemplate({ ...editingTemplate, ...payload, sections: orderedSections, is_default: isDefault });
      }
      setSections(orderedSections);
      setSaveMessage('Vorlage gespeichert!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  // --- Editor: Section operations ---

  const addSection = () => {
    const newSection: TemplateSection = {
      id: `section_${Date.now()}`,
      title: 'Neue Sektion',
      enabled: true,
      required: false,
      order: sections.length + 1,
      text: '',
      fields: [],
    };
    setSections([...sections, newSection]);
    setExpandedSections((prev) => ({ ...prev, [newSection.id]: true }));
  };

  const updateSection = (sectionId: string, updates: Partial<TemplateSection>) => {
    setSections((prev) => prev.map((s) => s.id === sectionId ? { ...s, ...updates } : s));
  };

  const deleteSection = (sectionId: string) => {
    if (!window.confirm('Sektion wirklich löschen?')) return;
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
    setExpandedSections((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  };

  const toggleSectionExpand = (sectionId: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // --- Editor: Field operations ---

  const addField = (sectionId: string) => {
    const newField: TemplateField = {
      id: `field_${Date.now()}`,
      label: 'Neues Feld',
      type: 'text',
      required: false,
    };
    setSections((prev) => prev.map((s) =>
      s.id === sectionId ? { ...s, fields: [...s.fields, newField] } : s
    ));
  };

  const updateField = (sectionId: string, fieldId: string, updates: Partial<TemplateField>) => {
    setSections((prev) => prev.map((s) =>
      s.id === sectionId
        ? { ...s, fields: s.fields.map((f) => f.id === fieldId ? { ...f, ...updates } : f) }
        : s
    ));
  };

  const deleteField = (sectionId: string, fieldId: string) => {
    setSections((prev) => prev.map((s) =>
      s.id === sectionId ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId) } : s
    ));
  };

  // --- Drag & Drop ---

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (targetIndex: number) => {
    const sourceIndex = dragIndexRef.current;
    if (sourceIndex === null || sourceIndex === targetIndex) {
      dragIndexRef.current = null;
      setDragOverIndex(null);
      return;
    }
    setSections((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(sourceIndex, 1);
      updated.splice(targetIndex, 0, moved);
      return updated.map((s, i) => ({ ...s, order: i + 1 }));
    });
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  // --- Render: Preview View (read-only) ---

  if (previewTemplate) {
    return (
      <div>
        <div className="flex-between mb-24">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setPreviewTemplate(null)}
              title="Zurück zur Liste"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h2 style={{ margin: 0 }}>{previewTemplate.name}</h2>
              <p className="text-muted text-sm" style={{ marginTop: 2 }}>
                Vorschau (nur lesen)
                {previewTemplate.is_system && <span className="badge badge-new" style={{ fontSize: '0.7rem', marginLeft: 8 }}>System</span>}
              </p>
            </div>
          </div>
          <div className="flex gap-8">
            {!previewTemplate.is_default && (
              <button
                className="btn btn-secondary"
                onClick={() => { handleSetDefault(previewTemplate.id); setPreviewTemplate({ ...previewTemplate, is_default: true }); }}
              >
                <Star size={16} /> Als Standard setzen
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={() => { handleDuplicate(previewTemplate.id); setPreviewTemplate(null); }}
            >
              <Copy size={16} /> Duplizieren &amp; Bearbeiten
            </button>
          </div>
        </div>

        {previewTemplate.description && (
          <p className="text-light" style={{ marginBottom: 16 }}>{previewTemplate.description}</p>
        )}

        {previewTemplate.sections.map((section, index) => (
          <div key={section.id} className="card" style={{ padding: 0, marginBottom: 12, opacity: section.enabled ? 1 : 0.5 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-light)' }}>
              <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text)' }}>
                &sect; {index + 1} &mdash; {section.title}
              </span>
              {section.required && (
                <span className="badge badge-active" style={{ fontSize: '0.7rem', marginLeft: 8 }}>Pflicht</span>
              )}
              {!section.enabled && (
                <span className="text-muted text-sm" style={{ marginLeft: 8 }}>(deaktiviert)</span>
              )}
            </div>
            <div style={{ padding: '14px 20px' }}>
              {section.text && (
                <p className="text-light text-sm" style={{ marginBottom: 12, whiteSpace: 'pre-wrap' }}>{section.text}</p>
              )}
              {section.fields.length > 0 && (
                <div style={{ display: 'grid', gap: 8 }}>
                  {section.fields.map((field) => (
                    <div
                      key={field.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        background: 'var(--bg)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-light)',
                      }}
                    >
                      <span style={{ fontWeight: 500, fontSize: '0.88rem', flex: 1 }}>{field.label}</span>
                      <span className="badge" style={{ fontSize: '0.72rem', background: 'var(--border-light)', color: 'var(--text-light)' }}>
                        {fieldTypeLabels[field.type] || field.type}
                      </span>
                      {field.required && (
                        <span style={{ color: 'var(--danger)', fontSize: '0.8rem' }}>*</span>
                      )}
                      {field.prefilled_from && (
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                          ← {field.prefilled_from}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {section.fields.length === 0 && !section.text && (
                <p className="text-muted text-sm">Keine Felder in dieser Sektion.</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // --- Render: Editor View ---

  if (editingTemplate || isCreating) {
    return (
      <div>
        {/* Header */}
        <div className="flex-between mb-24">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleBackToList}
              title="Zurück zur Liste"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h2 style={{ margin: 0 }}>
                {isCreating ? 'Neue Vorlage erstellen' : 'Vorlage bearbeiten'}
              </h2>
              <p className="text-muted text-sm" style={{ marginTop: 2 }}>
                {isCreating ? 'Erstelle eine neue Vertragsvorlage' : templateName}
              </p>
            </div>
          </div>
          <div className="flex gap-8">
            {saveMessage && (
              <span style={{
                color: 'var(--success)',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}>
                {saveMessage}
              </span>
            )}
            <button className="btn btn-secondary" onClick={handleBackToList}>
              Abbrechen
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Template metadata */}
        <div className="card" style={{ padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Vorlagenname</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="z.B. Standard-Coaching-Vertrag"
              />
            </div>
            <div className="form-group">
              <label>Beschreibung</label>
              <input
                type="text"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Kurze Beschreibung der Vorlage"
              />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label
              className="consent-checkbox-item"
              style={{ display: 'inline-flex', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              <span>Als Standard-Vorlage verwenden</span>
            </label>
          </div>
        </div>

        {/* Sections */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>
            <FileText size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Sektionen ({sections.length})
          </h3>
        </div>

        {sections.map((section, index) => {
          const isExpanded = expandedSections[section.id] || false;
          const isDragOver = dragOverIndex === index;

          return (
            <div
              key={section.id}
              className="card"
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={() => handleDrop(index)}
              onDragEnd={handleDragEnd}
              style={{
                padding: 0,
                marginBottom: 12,
                opacity: section.enabled ? 1 : 0.5,
                borderTop: isDragOver ? '3px solid var(--primary)' : '1px solid var(--border-light)',
                transition: 'opacity 0.2s, border-top 0.15s',
              }}
            >
              {/* Section header */}
              <div
                className="flex-between"
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  borderBottom: isExpanded ? '1px solid var(--border-light)' : 'none',
                }}
                onClick={() => toggleSectionExpand(section.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                  {/* Toggle enabled */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateSection(section.id, { enabled: !section.enabled });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: section.enabled ? 'var(--success)' : 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 0,
                    }}
                    title={section.enabled ? 'Sektion deaktivieren' : 'Sektion aktivieren'}
                  >
                    {section.enabled
                      ? <ToggleRight size={22} />
                      : <ToggleLeft size={22} />
                    }
                  </button>

                  {/* Drag handle */}
                  <span
                    style={{
                      cursor: 'grab',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    title="Ziehen zum Umsortieren"
                  >
                    <GripVertical size={18} />
                  </span>

                  <span style={{ fontWeight: 600, fontSize: '0.92rem', color: 'var(--text)' }}>
                    &sect; {index + 1} &mdash; {section.title}
                  </span>
                  {section.required && (
                    <span className="badge badge-active" style={{ fontSize: '0.7rem' }}>
                      Pflicht
                    </span>
                  )}
                  <span className="text-muted text-sm" style={{ marginLeft: 4 }}>
                    ({section.fields.length} {section.fields.length === 1 ? 'Feld' : 'Felder'})
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    className="btn btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSection(section.id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--danger)',
                      cursor: 'pointer',
                      padding: 4,
                    }}
                    title="Sektion löschen"
                  >
                    <Trash2 size={15} />
                  </button>
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* Section body (expanded) */}
              {isExpanded && (
                <div style={{ padding: '16px 20px' }}>
                  {/* Section title input */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 16 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Sektionstitel</label>
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => updateSection(section.id, { title: e.target.value })}
                        placeholder="Titel der Sektion"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end' }}>
                      <label className="consent-checkbox-item" style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={section.required || false}
                          onChange={(e) => updateSection(section.id, { required: e.target.checked })}
                        />
                        <span>Pflichtsektion</span>
                      </label>
                    </div>
                  </div>

                  {/* Section text */}
                  <div className="form-group">
                    <label>Vertragstext</label>
                    <textarea
                      value={section.text}
                      onChange={(e) => updateSection(section.id, { text: e.target.value })}
                      rows={4}
                      placeholder="Freitext für diese Vertragssektion..."
                      style={{ resize: 'vertical', minHeight: 80 }}
                    />
                  </div>

                  {/* Fields */}
                  <div style={{ marginTop: 16 }}>
                    <label style={{ marginBottom: 8, fontWeight: 600, fontSize: '0.88rem', color: 'var(--text)' }}>
                      Felder
                    </label>

                    {section.fields.length === 0 && (
                      <p className="text-muted text-sm" style={{ padding: '8px 0' }}>
                        Noch keine Felder in dieser Sektion.
                      </p>
                    )}

                    {section.fields.map((field) => (
                      <div
                        key={field.id}
                        style={{
                          background: 'var(--bg)',
                          border: '1px solid var(--border-light)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '12px 14px',
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                          <span className="text-muted" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                            {field.id}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto auto', gap: 10, alignItems: 'end' }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Label</label>
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => updateField(section.id, field.id, { label: e.target.value })}
                              placeholder="Feldbezeichnung"
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Typ</label>
                            <select
                              value={field.type}
                              onChange={(e) => {
                                const newType = e.target.value as TemplateField['type'];
                                const updates: Partial<TemplateField> = { type: newType };
                                if (newType !== 'select') {
                                  updates.options = undefined;
                                }
                                updateField(section.id, field.id, updates);
                              }}
                            >
                              {Object.entries(fieldTypeLabels).map(([val, label]) => (
                                <option key={val} value={val}>{label}</option>
                              ))}
                            </select>
                          </div>
                          <label
                            className="consent-checkbox-item"
                            style={{
                              marginBottom: 0,
                              padding: '8px 10px',
                              fontSize: '0.8rem',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={field.required || false}
                              onChange={(e) => updateField(section.id, field.id, { required: e.target.checked })}
                            />
                            <span>Pflicht</span>
                          </label>
                          <button
                            onClick={() => deleteField(section.id, field.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--danger)',
                              cursor: 'pointer',
                              padding: '8px 4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: '0.8rem',
                            }}
                            title="Feld entfernen"
                          >
                            <Trash2 size={14} />
                            <span>Entfernen</span>
                          </button>
                        </div>

                        {/* Placeholder input */}
                        <div style={{ marginTop: 8 }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Platzhalter (optional)</label>
                            <input
                              type="text"
                              value={field.placeholder || ''}
                              onChange={(e) => updateField(section.id, field.id, { placeholder: e.target.value })}
                              placeholder="Platzhaltertext..."
                              style={{ fontSize: '0.85rem' }}
                            />
                          </div>
                        </div>

                        {/* Prefilled_from input */}
                        <div style={{ marginTop: 8 }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Vorbefüllt aus (optional, z.B. coach_name, client_email)</label>
                            <input
                              type="text"
                              value={field.prefilled_from || ''}
                              onChange={(e) => updateField(section.id, field.id, { prefilled_from: e.target.value || undefined })}
                              placeholder="z.B. coach_name"
                              style={{ fontSize: '0.85rem' }}
                            />
                          </div>
                        </div>

                        {/* Select options */}
                        {field.type === 'select' && (
                          <div style={{ marginTop: 8 }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label>Optionen (kommagetrennt)</label>
                              <input
                                type="text"
                                value={(field.options || []).join(', ')}
                                onChange={(e) => {
                                  const opts = e.target.value
                                    .split(',')
                                    .map((o) => o.trim())
                                    .filter((o) => o.length > 0);
                                  updateField(section.id, field.id, { options: opts });
                                }}
                                placeholder="Option 1, Option 2, Option 3"
                                style={{ fontSize: '0.85rem' }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => addField(section.id)}
                      style={{ marginTop: 4 }}
                    >
                      <Plus size={14} /> Feld hinzufügen
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add section button */}
        <button
          className="btn btn-secondary"
          onClick={addSection}
          style={{ marginTop: 8, marginBottom: 24 }}
        >
          <Plus size={16} /> Sektion hinzufügen
        </button>

        {/* Bottom save bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            paddingTop: 16,
            borderTop: '1px solid var(--border-light)',
            marginTop: 8,
          }}
        >
          {saveMessage && (
            <span style={{
              color: 'var(--success)',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginRight: 'auto',
            }}>
              {saveMessage}
            </span>
          )}
          <button className="btn btn-secondary" onClick={handleBackToList}>
            Abbrechen
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    );
  }

  // --- Render: List View ---

  return (
    <div>
      <div className="flex-between mb-24">
        <div>
          <h2 style={{ margin: 0 }}>
            <FileText size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Vertragsvorlagen
          </h2>
          <p className="text-muted text-sm" style={{ marginTop: 4 }}>
            Verwalte deine Coaching-Vertragsvorlagen
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleCreate}>
          <Plus size={16} /> Neue Vorlage erstellen
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          color: '#991b1b',
          padding: '10px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: '0.9rem',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="empty-state">
          <p className="text-muted">Vorlagen werden geladen...</p>
        </div>
      ) : templates.length === 0 ? (
        <div className="empty-state">
          <FileText size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <p className="text-muted">Noch keine Vertragsvorlagen vorhanden.</p>
          <button className="btn btn-primary" onClick={handleCreate} style={{ marginTop: 16 }}>
            <Plus size={16} /> Erste Vorlage erstellen
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {templates.map((template) => (
            <div key={template.id} className="card" style={{ padding: '20px 24px' }}>
              <div className="flex-between" style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h3 style={{ margin: 0, fontSize: '1.05rem' }}>
                    {template.name}
                  </h3>
                  {template.is_default && (
                    <span
                      className="badge badge-ongoing"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Star size={12} /> Standard
                    </span>
                  )}
                  {template.is_system && (
                    <span className="badge badge-new" style={{ fontSize: '0.7rem' }}>
                      System
                    </span>
                  )}
                </div>
              </div>

              {template.description && (
                <p className="text-light text-sm" style={{ marginBottom: 6 }}>
                  {template.description}
                </p>
              )}

              <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
                {Array.isArray(template.sections) ? template.sections.length : 0} Sektionen
              </p>

              <div className="flex gap-8">
                {template.is_system ? (
                  <button className="btn btn-secondary btn-sm" onClick={() => handlePreview(template.id)}>
                    <Eye size={14} /> Ansehen
                  </button>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(template.id)}>
                    <FileText size={14} /> Bearbeiten
                  </button>
                )}
                {!template.is_default && (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleSetDefault(template.id)}>
                    <Star size={14} /> Als Standard
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => handleDuplicate(template.id)}>
                  <Copy size={14} /> Duplizieren
                </button>
                {!template.is_system && (
                  <button
                    className="btn btn-sm"
                    onClick={() => handleDelete(template.id)}
                    style={{
                      background: 'none',
                      border: '1px solid var(--danger)',
                      color: 'var(--danger)',
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={14} /> Löschen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
